import React, { useState } from 'react';
import { FileText, Truck, MapPin, Clock, CheckCircle2, Phone, Mail, ArrowRight, Globe, Shield, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const DocumentsToIndiaPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    documentType: ''
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Quote Request Received!",
      description: "Our team will contact you within 24 hours with document shipping rates to India."
    });
    setFormData({ name: '', email: '', phone: '', documentType: '' });
  };

  const benefits = [
    {
      icon: Clock,
      title: "Express Delivery",
      description: "Fast document courier to India with delivery in 3-7 business days via DHL, FedEx, and UPS express services."
    },
    {
      icon: Shield,
      title: "Secure Handling",
      description: "Professional document packaging and secure handling throughout the shipping process for complete peace of mind."
    },
    {
      icon: Truck,
      title: "Door-to-Door Service",
      description: "Convenient pickup from anywhere in Canada and direct delivery to any address across India."
    },
    {
      icon: MapPin,
      title: "Real-Time Tracking",
      description: "Track your documents every step of the way with live shipment updates from pickup to delivery."
    },
    {
      icon: FileText,
      title: "Customs Clearance",
      description: "Expert assistance with customs documentation to ensure smooth clearance of your documents in India."
    },
    {
      icon: Globe,
      title: "Nationwide Coverage",
      description: "Ship documents from any Canadian city to any destination in India with comprehensive coverage."
    }
  ];

  const documentTypes = [
    { type: 'Legal Documents', description: 'Contracts, affidavits, court papers, notarized documents' },
    { type: 'Educational Documents', description: 'Transcripts, degrees, certificates, academic records' },
    { type: 'Business Documents', description: 'Invoices, agreements, proposals, company paperwork' },
    { type: 'Immigration Documents', description: 'Visa applications, PR documents, passport copies' },
    { type: 'Financial Documents', description: 'Bank statements, tax records, financial reports' },
    { type: 'Personal Documents', description: 'Birth certificates, marriage certificates, IDs' }
  ];

  const steps = [
    {
      number: 1,
      title: "Prepare Your Documents",
      description: "Organize and securely package your documents. We recommend using envelopes or document folders for protection."
    },
    {
      number: 2,
      title: "Contact Shippzy",
      description: "Reach out via phone, email, or online form. Provide document details and destination address in India."
    },
    {
      number: 3,
      title: "Get Instant Quote",
      description: "We provide competitive rates from DHL, FedEx, and UPS. Choose express or economy service based on urgency."
    },
    {
      number: 4,
      title: "Schedule Pickup",
      description: "We arrange convenient pickup from your location or you can drop off at our partner locations across Canada."
    },
    {
      number: 5,
      title: "Track & Deliver",
      description: "Track your document shipment in real-time. Delivery confirmation provided upon arrival in India."
    }
  ];

  const faqs = [
    {
      question: "How long does document shipping from Canada to India take?",
      answer: "Express document courier services typically deliver in 2-5 business days. Standard document shipping takes 7-9 business days. Standard parcel delivery takes 3-7 business days. Transit time depends on the origin city in Canada, destination in India, and carrier service selected."
    },
    {
      question: "Can I send passport documents from Canada to India?",
      answer: "We can ship passport photocopies and supporting documents. However, we cannot ship actual passports or government-issued identity cards. Contact us to discuss your specific document shipping requirements."
    },
    {
      question: "What is the cost to send documents to India from Canada?",
      answer: "Document shipping costs depend on weight, dimensions, destination, and service speed. Express document courier typically starts around $30-$50 for small envelopes. We compare rates from DHL, FedEx, and UPS to provide the best pricing."
    },
    {
      question: "Do I need to declare documents for customs in India?",
      answer: "Yes, all international shipments require customs declaration. Shippzy helps you complete the necessary customs paperwork. Documents are generally duty-free, but proper documentation ensures smooth clearance."
    },
    {
      question: "Can you help with urgent document shipping to India?",
      answer: "Absolutely! We offer express document courier services with delivery in 3-5 business days to major Indian cities. Contact us for same-day pickup and expedited processing."
    },
    {
      question: "Which Canadian cities do you pickup documents from?",
      answer: "We provide document pickup services across Canada including Toronto, Brampton, Mississauga, Vancouver, Surrey, Calgary, Edmonton, Ottawa, Montreal, and other cities. We also have partner drop-off locations."
    },
    {
      question: "Can I track my document shipment to India?",
      answer: "Yes! All document shipments include full tracking. You'll receive a tracking number via email immediately after dispatch, allowing you to monitor your shipment's progress in real-time."
    },
    {
      question: "What documents cannot be sent to India?",
      answer: "We cannot ship original passports, actual currency, negotiable instruments, or sensitive government documents. Most legal, educational, business, and personal documents can be shipped. Contact us for specific restrictions."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Documents to India from Canada | Express Document Courier – Shippzy</title>
        <meta name="description" content="Send documents from Canada to India fast and secure. Express document courier with DHL, FedEx, UPS. Legal, educational, business documents. Door-to-door delivery in 3-7 days. Real-time tracking included." />
        <meta name="keywords" content="documents to India from Canada, send documents to India, document courier Canada to India, express document shipping India, send legal documents India, ship certificates to India, courier documents Canada India" />
        <link rel="canonical" href="https://www.shippzy.ca/documents-to-india-from-canada" />
        
        <meta property="og:title" content="Documents to India from Canada | Express Courier – Shippzy" />
        <meta property="og:description" content="Fast, secure document shipping from Canada to India. Express delivery in 3-7 days with tracking. Professional handling of legal, educational, and business documents." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/documents-to-india-from-canada" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Document Shipping to India from Canada",
            "provider": {
              "@type": "Organization",
              "name": "Shippzy Inc.",
              "telephone": "+1-437-616-0758",
              "email": "ship@shippzy.ca"
            },
            "areaServed": ["CA", "IN"],
            "serviceType": "Document Courier Service"
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                📄 Fast & Secure Document Courier
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Send Documents to India from Canada
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy provides fast and secure document courier services from Canada to India. Whether you need to send legal documents, educational certificates, business paperwork, or personal records, we offer express delivery in 3-7 business days through trusted carriers including DHL, FedEx, and UPS. Professional handling, door-to-door service, customs clearance assistance, and real-time tracking included.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Instant Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Shippzy Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Shippzy for Document Shipping to India</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Trusted document courier service with professional handling and fast delivery to any location in India
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Document Types Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Types of Documents We Ship to India</h2>
              <p className="text-xl text-gray-600">
                Professional courier service for all document types
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documentTypes.map((doc, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">{doc.type}</h3>
                        <p className="text-sm text-gray-600">{doc.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 p-6 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Important Restrictions</h4>
                  <p className="text-gray-700">
                    We cannot ship original passports, actual currency, negotiable instruments, or classified documents. Most legal, educational, business, and personal documents are acceptable. Contact us for specific document type verification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How to Send Documents from Canada to India</h2>
              <p className="text-xl text-gray-600">Simple 5-step process for secure document delivery</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about shipping documents to India</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA / Quote Form Section */}
        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Ready to Ship Your Documents to India?</h2>
              <p className="text-xl">Get your free document courier quote today</p>
            </div>

            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Get Your Free Quote</CardTitle>
                <CardDescription className="text-center">We'll provide the best rates for your document shipment</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Document Type</label>
                    <Input
                      name="documentType"
                      value={formData.documentType}
                      onChange={handleChange}
                      placeholder="e.g., Educational certificates, Legal documents"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg"
                  >
                    Get Quote Now
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center text-white">
              <p className="text-lg mb-4">Or contact us directly:</p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a href="tel:+14376160758" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Phone className="w-5 h-5" />
                  +1 (437) 616-0758
                </a>
                <a href="mailto:ship@shippzy.ca" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Mail className="w-5 h-5" />
                  ship@shippzy.ca
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Trusted Document Courier Service from Canada to India</h3>
            <p className="text-gray-300 mb-6">
              Fast, secure, and reliable document shipping with express delivery in 3-7 days. Professional handling of legal, educational, business, and personal documents. Door-to-door service with customs clearance support and real-time tracking.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => window.location.href = '/shipping-to-india-from-canada'}
              >
                Explore Canada to India Shipping
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                Back to Home
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DocumentsToIndiaPage;
